import {
  createApi,
} from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import UserDTO, { AuthUserData } from '../../models/user';
import { BusinessDTO } from '../../models/business';
import baseQueryWithReauth from './base';
import EventDTO from '../../models/event';

interface LogInData {
  email: string,
  password: string,
}

interface SignUpData {
  email: string;
  password: string;
  username: string;
  phone_number: string;
}

export interface LogInResponse {
  token: string;
  user_data: AuthUserData
}

export interface CustomError {
  data:{
    error: string,
    msg: string,
  },
  status: number
}

export interface GoogleLoginDTO {
  email: string;
  display_name: string;
  firebase_id: string
}

const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['users', 'user-signup', 'user-businesses', 'user-events'],
  endpoints: (build) => ({
    logIn: build.mutation<Response<LogInResponse>, LogInData>({
      query: (loginData) => ({
        url: '/users/login',
        method: 'POST',
        body: loginData,
      }),
    }),
    createUser: build.mutation<UserDTO | null, SignUpData>({
      query: (signUpData) => ({
        url: '/users',
        method: 'POST',
        body: signUpData,
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    getUserByFirebaseId: build.query<UserDTO | null, { firebaseId: string }>({
      providesTags: ['user-businesses'],
      query: ({ firebaseId }) => ({
        url: '/users/auth',
        params: {
          firebase_id: firebaseId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    getUserByConfirmationCode: build.query<UserDTO | null, { confirmationCode: string }>({
      providesTags: ['user-signup'],
      query: ({ confirmationCode }) => ({
        url: `/users/code/${confirmationCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    getBusinessUsers: build.query<UserDTO | null, { firebaseId: string }>({
      providesTags: ['user-businesses'],
      query: ({ firebaseId }) => ({
        url: '/users/auth',
        params: {
          firebase_id: firebaseId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    verifyUser: build.mutation<UserDTO | null, { userId: string, code: string }>({
      query: ({ userId, code }) => ({
        url: `/users/${userId}/verify`,
        method: 'POST',
        body: { verification_code: code },
      }),
      invalidatesTags: ['user-signup'],
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    getUserBusinesses: build.query<BusinessDTO[], { userId: string }>({
      providesTags: ['user-businesses'],
      query: ({ userId }) => ({
        url: `/users/${userId}/businesses`,
        method: 'GET',
      }),
      transformResponse: (response: Response<BusinessDTO[]>): (BusinessDTO[]) => response.data || [],
    }),
    validateGoogleLogin: build.mutation<UserDTO | null, GoogleLoginDTO>({
      query: (data) => ({
        url: '/users/google-login',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    getAllUserEvents: build.query<EventDTO[], { userId: string, businessId?: string }>({
      providesTags: ['user-events'],
      query: ({ userId, businessId }) => ({
        url: `/users/${userId}/events`,
        params: {
          business_id: businessId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<EventDTO[]>): (EventDTO[]) => response.data || [],
    }),
    getUser: build.query<UserDTO | null, { businessId: string, userId: string }>({
      query: ({ businessId, userId }) => ({
        url: `businesses/${businessId}/users/${userId}`,
        params: {
          user_id: userId,
        },
        method: 'GET',
      }),
      transformResponse: (response: Response<UserDTO>): (UserDTO | null) => response.data || null,
    }),
    sendRecoverPasswordEmail: build.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: '/users/forgot-password',
        method: 'POST',
        body: { email },
      }),
    }),
    resetPassword: build.mutation<void, { token: string, new_password: string }>({
      query: ({ token, new_password }) => ({
        url: '/users/reset-password',
        method: 'POST',
        body: { token, new_password },
      }),
    }),
  }),
});

export const {
  useLogInMutation,
  useGetUserByFirebaseIdQuery,
  useCreateUserMutation,
  useGetUserByConfirmationCodeQuery,
  useVerifyUserMutation,
  useGetUserBusinessesQuery,
  useValidateGoogleLoginMutation,
  useLazyGetUserByFirebaseIdQuery,
  useGetAllUserEventsQuery,
  useGetUserQuery,
  useSendRecoverPasswordEmailMutation,
  useResetPasswordMutation,
} = usersApi;
export { usersApi };
